import { ReactElement, use } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import WanderingBoundaryIcon from 'assets/icons/wandering-boundary.svg?react';
import Constants from 'style/constants';
import WanderingBoundaryPassageIcon from 'shared/components/icons/wandering-boundary-passage-icon';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';

import SettingTabs from 'features/settings/settings-tabs';
import WanderingBoundaryLocation from 'features/wandering-boundary-settings/domain/models/wandering-boundary-location';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';
import WanderingBoundarySettingsDetails from 'features/wandering-boundary-settings/views/wandering-boundary-settings-details/wandering-boundary-settings-details.component';
import styles from './wandering-boundary-settings.module.scss';

const WanderingBoundarySettings = (): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');

  const wanderingBoundaryPassageIconStyle = { width: '16px', height: '16px', marginLeft: 'auto' };

  const {
    detailsState: { selectedLocation: currentSelectedWanderingBoundaryLocation, toggleDetails },
    readWanderingBoundaryLocationsIsLoading,
    readWanderingBoundaryLocationsData,
  } = use(WanderingBoundarySettingsProviderContext);

  const icons = (wanderingBoundaryLocation: WanderingBoundaryLocation) => (
    <div className={styles.iconRow}>
      {wanderingBoundaryLocation.hasWanderingBoundaries && (
        <Tooltip title={t('iconTooltips.wanderingBoundary')}>
          <WanderingBoundaryIcon className={styles.wanderingBoundaryIcon} />
        </Tooltip>
      )}
      {wanderingBoundaryLocation.hasWanderingBoundaryPassages && (
        <WanderingBoundaryPassageIcon
          sx={wanderingBoundaryPassageIconStyle}
          color={Constants.Colors.onSurface}
          tooltipText={t('iconTooltips.wanderingBoundaryPassage')}
        />
      )}
    </div>
  );

  const columns: ColumnDefinition<WanderingBoundaryLocation, keyof WanderingBoundaryLocation>[] = [
    {
      key: 'name',
      label: t('table.columns.name'),
    },
    {
      key: 'hasWanderingBoundaries',
      label: '',
      renderCustomContentProvider: icons,
      disableSort: true,
    },
  ];

  const selectedItem = readWanderingBoundaryLocationsData?.find(
    (wanderingBoundaryLocation) => wanderingBoundaryLocation.id === currentSelectedWanderingBoundaryLocation?.id,
  );

  return (
    <>
      <PageHeader title={t('pageHeaderTitle')} navigationComponent={<SettingTabs />} />
      <div className={styles.container}>
        <div className={styles.leftContent}>
          {readWanderingBoundaryLocationsIsLoading && <LoadingIndicator />}
          {readWanderingBoundaryLocationsData && readWanderingBoundaryLocationsData?.length > 0 && (
            <Table
              data={readWanderingBoundaryLocationsData.map<RowItem<WanderingBoundaryLocation>>((location) => ({ data: location }))}
              columns={columns}
              currentItem={selectedItem}
              onItemClick={(item: WanderingBoundaryLocation) => toggleDetails(item)}
              rowIdentifier={(item: WanderingBoundaryLocation) => item.id}
              useBuiltInSorter={true}
            />
          )}
        </div>
        <WanderingBoundarySettingsDetails />
      </div>
    </>
  );
};

export default WanderingBoundarySettings;
